import { apiErrorMessage, isApiError } from "@/core/api";
import { formatDate, formatRelative } from "@/core/i18n";
import { cn } from "@/lib/utils";
import { useGetArchivedProjectLabelsQuery } from "@/modules/project/services/projectApi";
import { Button } from "@/modules/shadcn/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/modules/shadcn/ui/popover";
import { Archive, ArchiveRestore } from "lucide-react";
import { RefObject, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ArchivedLabelsPopoverProps {
  projectId: number | undefined;
  /** Element the panel sits above (the whole toolbar); defaults to the trigger. */
  anchor?: RefObject<HTMLElement | null>;
  /** The open image has unsaved edits: a restore could be overwritten, so it waits. */
  hasUnsavedChanges: boolean;
  /** Resolves on success; throws to leave the row in place with an inline error. */
  onRestore: (labelId: number) => Promise<void>;
}

/**
 * End of the class toolbar: lists the project's archived labels with when they
 * were archived and how many regions each one hides, each with a Restore.
 * Renders nothing while no label is archived, so projects that never archive
 * keep the toolbar as it was.
 */
export const ArchivedLabelsPopover = ({
  projectId,
  anchor,
  hasUnsavedChanges,
  onRestore,
}: ArchivedLabelsPopoverProps) => {
  const { t } = useTranslation(["label", "common"]);
  const [open, setOpen] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: archived = [] } = useGetArchivedProjectLabelsQuery(
    { projectId: projectId ?? 0 },
    { skip: !projectId },
  );

  if (archived.length === 0) return null;

  const restore = async (labelId: number) => {
    setRestoringId(labelId);
    setError(null);
    try {
      await onRestore(labelId);
      // The last one coming back removes the button, and this panel with it.
    } catch (err) {
      setError(
        isApiError(err)
          ? apiErrorMessage(err, t("archivedLabels.restoreFailed"))
          : t("archivedLabels.restoreFailed"),
      );
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setError(null);
      }}
    >
      <PopoverTrigger
        aria-label={t("archivedLabels.triggerAria", { total: archived.length })}
        title={t("archivedLabels.title")}
        className="relative flex size-[34px] shrink-0 cursor-pointer items-center justify-center rounded-lg text-foreground/60 transition-colors outline-none hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-ring/50 data-popup-open:bg-black/5"
      >
        <Archive className="size-5" />
        <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground/70 px-1 text-[10px] leading-none font-semibold text-white tabular-nums">
          {archived.length}
        </span>
      </PopoverTrigger>
      <PopoverContent
        anchor={anchor}
        align="end"
        side="top"
        sideOffset={8}
        className="w-[340px] gap-2 rounded-2xl p-4"
      >
        <p className="text-sm font-medium">{t("archivedLabels.title")}</p>
        <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto">
          {archived.map((label) => (
            <li
              key={label.id}
              className="flex items-center gap-2.5 rounded-lg bg-black/[0.03] py-1.5 pr-1.5 pl-2.5"
            >
              <span
                className="block size-2.5 shrink-0 rounded-[3px] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.12)]"
                style={{ background: label.color }}
              />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-[13px] leading-[18px] text-foreground/90">
                  {label.name}
                </span>
                <span className="truncate text-[11px] leading-4 text-muted-foreground">
                  {label.deletedAt && (
                    <time
                      dateTime={label.deletedAt}
                      title={formatDate(label.deletedAt)}
                    >
                      {t("archivedLabels.archivedOn", {
                        when: formatRelative(label.deletedAt),
                      })}
                    </time>
                  )}
                  {" · "}
                  {t("archivedLabels.regions", { count: label.regionCount })}
                </span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={restoringId !== null || hasUnsavedChanges}
                aria-label={t("archivedLabels.restoreAria", {
                  name: label.name,
                })}
                onClick={() => void restore(label.id)}
                className={cn("shrink-0 gap-1.5")}
              >
                <ArchiveRestore className="size-3.5" />
                {t("archivedLabels.restore")}
              </Button>
            </li>
          ))}
        </ul>
        {hasUnsavedChanges && (
          <p className="text-xs text-muted-foreground">
            {t("archivedLabels.saveFirst")}
          </p>
        )}
        {error && <p className="text-xs text-destructive">{error}</p>}
      </PopoverContent>
    </Popover>
  );
};
